// Shared vocabulary for the website's own statistics (no third party, no cookies).
// Events are recorded by /api/track (see src/components/analytics/Tracker.tsx) and summed up for /admin/stats.

export type Device = "phone" | "tablet" | "desktop";

/** One recorded event. Short keys keep the daily files small. */
export type StatEvent = {
  /** Time (ms since 1970). */
  t: number;
  /** view: a page view · time: seconds on a page · read: % of an article reached · click: a call to action
   *  char: a character interaction · vital: page speed · 404: a missing page · lead: a contact form message */
  e: "view" | "time" | "read" | "click" | "char" | "vital" | "404" | "lead";
  /** Page path. */
  p: string;
  /** Anonymous visitor id: a one-way code that changes every day (no cookies, no IP stored). */
  v: string;
  /** Where the visit came from (Google, Instagram, Direct…). */
  s: string;
  d: Device;
  /** Country or region, from the visitor's time zone. */
  r: string;
  /** Detail: which button, which character action, which speed metric, which budget. */
  k?: string;
  /** Number: seconds, % read, or the speed value. */
  n?: number;
};

export const EVENT_TYPES = ["view", "time", "read", "click", "char", "vital", "404", "lead"] as const;

/** Path only (no query or hash), never the admin. */
export const cleanPath = (path: unknown) => {
  const p = String(path ?? "").split(/[?#]/)[0].slice(0, 200);
  return p.startsWith("/") && !p.startsWith("/admin") ? p : null;
};

/** The events a browser may send (leads are recorded by the contact form itself). */
const PUBLIC_EVENTS = new Set<string>(EVENT_TYPES.filter((e) => e !== "lead"));

/**
 * One event from a browser's batch (src/lib/track.ts), checked and cleaned, or null to drop it. The same rules for
 * /api/track on our own server and in the edge Worker: known type, a real page path, the detail lower-cased to
 * [a-z0-9:_-] (40 characters), the number finite, ≥ 0 and capped (30 minutes for time on page).
 */
export function cleanIncomingEvent(item: unknown, visit: Pick<StatEvent, "v" | "s" | "d" | "r">, t: number): StatEvent | null {
  const raw = (item ?? {}) as Record<string, unknown>;
  const type = String(raw.e ?? "");
  const p = cleanPath(raw.p);
  if (!PUBLIC_EVENTS.has(type) || !p) return null;
  const event: StatEvent = { t, e: type as StatEvent["e"], p, ...visit };
  const k = String(raw.k ?? "").toLowerCase().replace(/[^a-z0-9:_-]/g, "").slice(0, 40);
  if (k) event.k = k;
  const n = Number(raw.n);
  if (raw.n !== undefined && raw.n !== null && Number.isFinite(n) && n >= 0) event.n = Math.min(n, type === "time" ? 1800 : 1e6);
  return event;
}

// ---------- classification ----------

const TIMEZONES: Record<string, string> = {
  "Africa/Cairo": "Egypt",
  "Asia/Riyadh": "Saudi Arabia",
  "Asia/Dubai": "UAE",
  "Asia/Qatar": "Qatar",
  "Asia/Kuwait": "Kuwait",
  "Asia/Bahrain": "Bahrain",
  "Asia/Muscat": "Oman",
};

/** Country (Egypt and the GCC by name) or wider region, from a time zone like "Africa/Cairo". */
export function regionFromTimezone(tz: string) {
  if (TIMEZONES[tz]) return TIMEZONES[tz];
  const area = tz.split("/")[0];
  if (area === "Europe") return "Europe";
  if (area === "America") return "Americas";
  if (area === "Asia") return "Other Asia";
  if (area === "Africa") return "Other Africa";
  if (area === "Australia" || area === "Pacific") return "Oceania";
  return "Unknown";
}

const SOURCES: [RegExp, string][] = [
  [/(^|\.)google\./, "Google"],
  [/(^|\.)bing\.com$/, "Bing"],
  [/duckduckgo\.com$/, "DuckDuckGo"],
  [/(^|\.)yahoo\./, "Yahoo"],
  [/chatgpt\.com$|openai\.com$/, "ChatGPT"],
  [/perplexity\.ai$/, "Perplexity"],
  [/claude\.ai$/, "Claude"],
  [/instagram\.com$/, "Instagram"],
  [/facebook\.com$|fb\.com$|fb\.me$/, "Facebook"],
  [/linkedin\.com$|lnkd\.in$/, "LinkedIn"],
  [/(^|\.)x\.com$|twitter\.com$|t\.co$/, "X"],
  [/tiktok\.com$/, "TikTok"],
  [/youtube\.com$|youtu\.be$/, "YouTube"],
  [/whatsapp\.com$|wa\.me$/, "WhatsApp"],
];

/** A readable source from a campaign tag (?utm_source=…) or the referring site's address. */
export function sourceFrom(utm: string | null, referrer: string | null, ownHost: string) {
  if (utm) {
    const clean = utm.trim().toLowerCase().slice(0, 40);
    const known = SOURCES.find(([, name]) => name.toLowerCase() === clean);
    return known ? known[1] : clean.replace(/^\w/, (c) => c.toUpperCase());
  }
  if (!referrer) return "Direct";
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, "");
    if (!host || host === ownHost) return "Direct";
    return SOURCES.find(([pattern]) => pattern.test(host))?.[1] ?? host;
  } catch {
    return "Direct";
  }
}

export const deviceFromWidth = (width: number): Device => (width < 768 ? "phone" : width < 1024 ? "tablet" : "desktop");

/** Search engines, link previews, uptime checkers and automated browsers aren't visitors. */
export const isBot = (userAgent: string) =>
  !userAgent || /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|preview|facebookexternalhit|whatsapp|telegram|curl|wget|python|node-fetch|axios|monitor/i.test(userAgent);

// ---------- summing up ----------

export const DAY_MS = 24 * 60 * 60 * 1000;
export const dayKey = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Unique visitors: the daily anonymous ids, counted per day and added up (ids change daily by design). */
export function visitorsOf(events: StatEvent[]) {
  const seen = new Set<string>();
  for (const e of events) if (e.e === "view") seen.add(`${dayKey(e.t)}|${e.v}`);
  return seen.size;
}

export function percentile(values: number[], p: number) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))];
}

export const average = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);

/** Change against the previous period, as a whole percentage (null when there's nothing to compare with). */
export const change = (now: number, before: number) => (before ? Math.round(((now - before) / before) * 100) : null);

/** Google's thresholds for the three page-speed measures (Core Web Vitals). */
export const VITALS = {
  LCP: { label: "Loading (LCP)", unit: "s", good: 2500, poor: 4000, format: (v: number) => `${(v / 1000).toFixed(1)} s` },
  INP: { label: "Responsiveness (INP)", unit: "ms", good: 200, poor: 500, format: (v: number) => `${Math.round(v)} ms` },
  CLS: { label: "Visual stability (CLS)", unit: "", good: 0.1, poor: 0.25, format: (v: number) => v.toFixed(2) },
} as const;
export type VitalName = keyof typeof VITALS;
